/**
 * Globaler Auth-Context.
 *
 * Beim Mount wird /api/auth/me gepolled; der HttpOnly-Cookie ist die einzige
 * Wahrheit über den Login-Status. Ein 401 aus /me ist nicht fatal — es bedeutet
 * einfach "nicht eingeloggt" und wird zu `user: null`.
 *
 * Nach Magic-Link-Verify ruft die Verify-Seite `refresh()` auf, damit der
 * Context den frischen User-State übernimmt, ohne einen vollen Page-Reload.
 */

import { createContext, useCallback, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { me, logout as apiLogout, type AuthUser, ApiError } from "./api";

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { user } = await me();
      setUser(user);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setUser(null);
      } else {
        // Netzwerkfehler — laut, aber nicht crashen
        console.error("[auth] /me failed:", err);
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } catch (err) {
      console.warn("[auth] logout API error, clearing local state anyway:", err);
    }
    setUser(null);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return <Ctx.Provider value={{ user, loading, refresh, logout }}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
